/**
 * useOnboarding — Manages new-user onboarding state
 *
 * Tracks whether the user has completed the onboarding wizard.
 * Uses localStorage to persist across sessions.
 *
 * Note: With free-tier LLM available, onboarding no longer gates on API
 * key configuration. It shows for all first-time users.
 */
import { useState, useCallback, useMemo } from 'react';
import { useAuthStore } from '@/shared/stores/authStore';

const STORAGE_KEY = 'novelforge-onboarding-completed';

/** Read completed-user list from localStorage */
function getCompletedUsers(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** Mark a user as having completed onboarding */
function markCompleted(userId: string): void {
  const users = getCompletedUsers();
  if (!users.includes(userId)) {
    users.push(userId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
  }
}

export interface OnboardingState {
  /** Whether the onboarding wizard should be shown */
  shouldShow: boolean;
  /** Complete onboarding — hides wizard and persists */
  complete: () => void;
  /** Skip onboarding — same as complete but semantically different */
  skip: () => void;
  /** Reset onboarding for the current user (mainly for testing) */
  reset: () => void;
}

export function useOnboarding(): OnboardingState {
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const [dismissed, setDismissed] = useState(false);

  const isCompleted = useMemo(() => {
    if (!user) return true; // Not logged in → don't show
    return getCompletedUsers().includes(user.id);
  }, [user]);

  const shouldShow = useMemo(() => {
    if (!initialized || !user) return false;
    if (isCompleted || dismissed) return false;
    // With free tier available, onboarding is about feature intro, not API config gate
    // Show for first-time users who haven't completed onboarding
    return true;
  }, [initialized, user, isCompleted, dismissed]);

  const complete = useCallback(() => {
    if (user) markCompleted(user.id);
    setDismissed(true);
  }, [user]);

  const skip = useCallback(() => {
    if (user) markCompleted(user.id);
    setDismissed(true);
  }, [user]);

  const reset = useCallback(() => {
    if (!user) return;
    const users = getCompletedUsers().filter((id) => id !== user.id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
    setDismissed(false);
  }, [user]);

  return { shouldShow, complete, skip, reset };
}
